import { getToken } from '@/utils/auth'

const INSIGHTS_URL = '/new-agent/alarm-analysis/insights'

export class AlarmInsightsRequestError extends Error {
  constructor(message, status, details) {
    super(message)
    this.name = 'AlarmInsightsRequestError'
    this.status = status
    this.details = details
  }
}

function getErrorMessage(status, detail) {
  if (status === 400) return detail?.message || '智能分析请求缺少必要参数'
  if (status === 422) return '请求数据格式错误，请重新获取告警分析数据'
  if (status === 401) return '登录状态已失效，请重新登录'
  if (status === 403) return '当前账号没有告警智能分析权限'
  return detail?.message || detail?.detail || `智能分析请求失败（HTTP ${status}）`
}

function newRequestId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * 发起告警图表解读并逐条消费 SSE 事件。
 * @param {Object} context AlarmChartAnalysisContext
 * @param {(event: Object) => void} onEvent
 * @param {AbortSignal} [signal]
 * @param {Object} [requestContext]
 * @param {string|number} [requestContext.userId]
 * @param {string} [requestContext.appId]
 */
export async function consumeAlarmInsights(context, onEvent, signal, requestContext = {}) {
  if (!requestContext.userId) {
    throw new AlarmInsightsRequestError('缺少用户标识，无法发起智能分析请求')
  }
  const token = getToken()
  const language = window.$str?.getLanguage?.() || 'zh-CN'
  const requestId = newRequestId()
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Language: language,
    'System-Sign': 'gemini3',
    'X-App-Id': String(requestContext.appId || 'eMind'),
    'X-User-Id': String(requestContext.userId || ''),
    'X-Session-Id': requestId,
    'X-Chat-Id': String(context.contextId || requestId),
    'X-Trace-Id': requestId,
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(INSIGHTS_URL, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ context, locale: 'zh-CN', stream: true }),
    signal,
  })

  if (!response.ok) {
    const detail = await response.json().catch(() => null)
    throw new AlarmInsightsRequestError(getErrorMessage(response.status, detail), response.status, detail)
  }
  if (!response.body) {
    throw new AlarmInsightsRequestError('浏览器未返回可读的分析数据流', response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
    const lines = buffer.split(/\r?\n/)
    buffer = lines.pop() || ''

    for (const line of lines) {
      emitSseLine(line, onEvent)
    }
    if (done) break
  }

  if (buffer) emitSseLine(buffer, onEvent)
}

function emitSseLine(line, onEvent) {
  if (!line.startsWith('data:')) return
  const payload = line.slice(5).trimStart()
  if (!payload) return

  try {
    onEvent(JSON.parse(payload))
  } catch (error) {
    console.warn('忽略无法解析的告警图表解读 SSE 数据', error)
  }
}
